import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { emailValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import type { RoleExternal } from "./organizationContactsState";

export interface EmailContactRow extends RoleExternal {
  id: string;
}

export const emptyEmailRow = (): EmailContactRow => ({ id: generateGuid(), name: "", email: "" });

export interface EmailRowError {
  name?: string;
  email?: string;
}

export interface EmailRowsValidation {
  rowErrors: EmailRowError[];
  contactsToAdd: RoleExternal[];
  isValid: boolean;
}

export const validateEmailContactRows = (rows: RoleExternal[]): EmailRowsValidation => {
  const normalizedRows = rows.map((row) => ({ name: row.name.trim(), email: sanitizeEmail(row.email) }));

  const emailCounts = new Map<string, number>();

  normalizedRows.forEach((row) => {
    const key = row.email.toLowerCase();
    if (key) {
      emailCounts.set(key, (emailCounts.get(key) ?? 0) + 1);
    }
  });

  const rowErrors = normalizedRows.map((row) => {
    if (!row.name && !row.email) {
      return {};
    }
    const error: EmailRowError = {};
    if (!requiredValidator(row.name).isValid) {
      error.name = "Name is required";
    }
    if (!emailValidator(row.email).isValid || !row.email) {
      error.email = "Invalid email address";
    } else if ((emailCounts.get(row.email.toLowerCase()) ?? 0) > 1) {
      error.email = "Duplicate email address";
    }
    return error;
  });

  const contactsToAdd = normalizedRows.filter((row) => row.name || row.email);
  const isValid = contactsToAdd.length > 0 && rowErrors.every((error) => !error.name && !error.email);

  return { rowErrors, contactsToAdd, isValid };
};
